import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { MANUAL_CREDIT_REASON_TYPES } from '@/components/customers/credits/CreateManualCreditModal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SearchableCustomerSelect from '@/components/shared/SearchableCustomerSelect';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';
import FormActions from '@/components/shared/FormActions';

const formSchema = z.object({
  customer_id: z.string({ required_error: 'Customer is required' }).min(1, 'Customer is required'),
  amount: z.number({ required_error: 'Amount is required' }).positive('Amount must be greater than 0'),
  reason_type: z.string({ required_error: 'Reason type is required' }).min(1, 'Reason type is required'),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
  internal_description: z.string().max(1000, 'Internal description must be 1000 characters or less').optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ManualCreditFormProps {
  onSave: () => void;
  onCancel: () => void;
}

const ManualCreditForm: React.FC<ManualCreditFormProps> = ({ onSave, onCancel }) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      customer_id: '',
      amount: undefined,
      reason_type: '',
      description: '',
      internal_description: '',
    },
  });

  const { data: customers } = useQuery({
    queryKey: ['customers-for-credit', profile?.company_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name, last_name, customer_type, business_name, representative_first_name, representative_last_name');
      if (error) throw error;
      return (data || []).sort((a, b) =>
        getCustomerDisplayName(a).localeCompare(getCustomerDisplayName(b))
      );
    },
    enabled: !!profile?.company_id,
  });

  const createMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userProfile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!userProfile?.company_id) throw new Error('Company not found');

      const { data: creditNoteNumber, error: numberError } = await supabase
        .rpc('generate_credit_note_number', { p_company_id: userProfile.company_id });

      if (numberError) throw numberError;

      const { error } = await supabase
        .from('customer_credits')
        .insert({
          customer_id: values.customer_id,
          credit_amount: values.amount,
          remaining_amount: values.amount,
          source_type: 'manual',
          reason_type: values.reason_type,
          description: values.description || null,
          internal_description: values.internal_description || null,
          created_by: user.id,
          status: 'issued',
          credit_note_number: creditNoteNumber,
          credit_note_issued_at: new Date().toISOString(),
          credit_note_issued_by: user.id,
        });

      if (error) throw error;
      return { creditNoteNumber };
    },
    onSuccess: (result) => {
      toast.success(`Credit ${result.creditNoteNumber} created successfully`);
      queryClient.invalidateQueries({ queryKey: ['company-credits'] });
      onSave();
    },
    onError: (error) => {
      toast.error(`Failed to create credit: ${error.message}`);
    },
  });

  const handleSubmit = (values: FormValues) => {
    createMutation.mutate(values);
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="customer_id">Customer *</Label>
        <SearchableCustomerSelect
          value={form.watch('customer_id')}
          onValueChange={(value) => form.setValue('customer_id', value, { shouldValidate: true })}
          customers={customers}
        />
        {form.formState.errors.customer_id && (
          <p className="text-sm text-destructive">{form.formState.errors.customer_id.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="amount">Amount *</Label>
        <Input
          id="amount"
          type="number"
          step="0.01"
          min="0.01"
          placeholder="0.00"
          {...form.register('amount', { valueAsNumber: true })}
        />
        {form.formState.errors.amount && (
          <p className="text-sm text-destructive">{form.formState.errors.amount.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="reason_type">Reason *</Label>
        <Select
          value={form.watch('reason_type')}
          onValueChange={(value) => form.setValue('reason_type', value, { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a reason" />
          </SelectTrigger>
          <SelectContent>
            {MANUAL_CREDIT_REASON_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {form.formState.errors.reason_type && (
          <p className="text-sm text-destructive">{form.formState.errors.reason_type.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Credit Note Description</Label>
        <Textarea
          id="description"
          placeholder="Description shown on the credit note PDF..."
          rows={3}
          {...form.register('description')}
        />
        <p className="text-xs text-muted-foreground">This will appear on the credit note document sent to the customer.</p>
        {form.formState.errors.description && (
          <p className="text-sm text-destructive">{form.formState.errors.description.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="internal_description">Internal Notes</Label>
        <Textarea
          id="internal_description"
          placeholder="Internal context for why this credit was issued..."
          rows={3}
          {...form.register('internal_description')}
        />
        <p className="text-xs text-muted-foreground">For internal use only. Not shown on credit note documents.</p>
        {form.formState.errors.internal_description && (
          <p className="text-sm text-destructive">{form.formState.errors.internal_description.message}</p>
        )}
      </div>

      <FormActions
        onCancel={onCancel}
        isSubmitting={createMutation.isPending}
        submitLabel="Create Credit"
        submittingLabel="Creating..."
      />
    </form>
  );
};

export default ManualCreditForm;
